import React, { ChangeEvent, Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { delay } from "@formant/core/common/delay";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { IAwsInfo } from "@formant/core/model/IAwsInfo";
import { IValidation } from "@formant/core/model/IValidation";
import { Uuid } from "@formant/core/model/Uuid";
import { InputElement } from "@formant/ui/component/common/Field";
import { TextField } from "@formant/ui/component/common/TextField";
import { animate } from "@formant/ui/utils/animate";

import { organizationStore } from "../../../stores";
import { Buttons } from "../../common/Buttons";
import { ContentTabs, IContentTab } from "../../common/ContentTabs";
import { CopyCode } from "../../common/CopyCode";
import { Heading3 } from "../../common/Heading3";
import { IconButton } from "../../common/IconButton";
import { StandardButton } from "../../common/StandardButton";
import { TextButton } from "../../common/TextButton";
import { ValidationMessages } from "../../common/ValidationMessages";
import * as styles from "./EditAwsInfo.scss";

export interface IEditAwsInfoProps {
    organizationId: Uuid;
    externalId: Uuid;
    awsInfo: IAwsInfo;
    awsAccountId: string;
    onClose: () => void;
}

@observer
export class EditAwsInfo extends Component<IEditAwsInfoProps> {
    @observable
    private awsInfo: IAwsInfo = this.props.awsInfo;
    @observable
    private hidden: boolean = true;
    @observable
    private validation?: IValidation;
    private externalId = this.props.externalId;

    public async componentDidMount() {
        await delay();
        this.hidden = false;
        this.awsInfo.roleName = "FormantAccessRole";
    }

    public render() {
        const { s3Export } = this.awsInfo;

        const s3Policy = {
            Version: "2012-10-17",
            Statement: [
                {
                    Effect: "Allow",
                    Action: ["s3:PutObject", "s3:GetObject"],
                    Resource: `arn:aws:s3:::${s3Export?.bucket || "*"}/*`,
                },
            ],
        };

        const tabs: IContentTab[] = [
            {
                name: "Configure integration",
                content: (
                    <div>
                        <ul>
                            <li>
                                <p>
                                    Your IAM account ID is unique 12-digit
                                    number, and can be found{" "}
                                    <TextButton
                                        to="https://console.aws.amazon.com/iam/home#/home"
                                        target="_blank"
                                    >
                                        here
                                    </TextButton>
                                </p>
                                <TextField
                                    className={styles.field}
                                    label="Account ID"
                                    small={true}
                                    value={this.awsInfo.accountId}
                                    invalid={
                                        !!this.validation?.["awsInfo.accountId"]
                                    }
                                    onChange={this.onChangeAccountId}
                                />
                            </li>
                            <li>
                                <p>
                                    The name of your S3 bucket.{" "}
                                    <TextButton
                                        to="https://s3.console.aws.amazon.com/s3/home"
                                        target="_blank"
                                    >
                                        Create a new bucket
                                    </TextButton>
                                </p>
                                <TextField
                                    className={styles.field}
                                    label="Bucket"
                                    small={true}
                                    value={s3Export?.bucket || ""}
                                    invalid={
                                        !!this.validation?.[
                                            "awsInfo.s3Export.bucket"
                                        ]
                                    }
                                    onChange={this.onChangeBucket}
                                />
                            </li>
                            <li>
                                <p>
                                    The path to export to within your S3 bucket
                                    (optional)
                                </p>
                                <TextField
                                    className={styles.field}
                                    label="Prefix"
                                    small={true}
                                    value={s3Export?.path || ""}
                                    invalid={
                                        !!this.validation?.[
                                            "awsInfo.s3Export.path"
                                        ]
                                    }
                                    onChange={this.onChangePath}
                                />
                            </li>
                        </ul>
                    </div>
                ),
            },
            {
                name: "Create a policy",
                content: (
                    <div>
                        <ol className={styles["numbered-list"]}>
                            <li>
                                <p>
                                    <TextButton
                                        to="https://console.aws.amazon.com/iam/home?#/policies$new?step=edit"
                                        target="_blank"
                                    >
                                        Go to the IAM policy editor
                                    </TextButton>
                                </p>
                            </li>
                            <li>
                                <div>
                                    <p>
                                        Paste the following in the JSON editor
                                    </p>
                                    <CopyCode
                                        value={JSON.stringify(s3Policy)}
                                    />
                                    <p>
                                        Click <b>Review policy</b>
                                    </p>
                                </div>
                            </li>
                            <li>
                                <div>
                                    <p>Set your policy name to</p>
                                    <CopyCode value="FormantAccessPolicy" />
                                    <p>
                                        Click <b>Create policy</b>
                                    </p>
                                </div>
                            </li>
                        </ol>
                    </div>
                ),
            },
            {
                name: "Create a role",
                content: (
                    <div>
                        <ol className={styles["numbered-list"]}>
                            <li>
                                <p>
                                    <TextButton
                                        to="https://console.aws.amazon.com/iam/home?#/roles$new?step=type&roleType=crossAccount"
                                        target="_blank"
                                    >
                                        Create a new Cross-account role
                                    </TextButton>
                                </p>
                            </li>
                            <li>
                                <div>
                                    <p>
                                        Paste the following into to the{" "}
                                        <i>Account ID</i> field
                                    </p>
                                    <CopyCode value={this.props.awsAccountId} />
                                    <p>
                                        Check the option{" "}
                                        <i>Require external ID</i>, and paste
                                        the following into the{" "}
                                        <i>External ID</i> field
                                    </p>
                                    <CopyCode value={this.externalId} />
                                    <p>
                                        Click <b>Next: Permissions</b>
                                    </p>
                                </div>
                            </li>
                            <li>
                                <div>
                                    <p>
                                        Click the checkbox next to{" "}
                                        <code>FormantAccessPolicy</code> and
                                        then continue.
                                    </p>
                                </div>
                            </li>
                            <li>
                                <div>
                                    <p>Add optional tags for traceability.</p>
                                    <p>
                                        When you're done, click{" "}
                                        <b>Next: Review</b>
                                    </p>
                                </div>
                            </li>
                            <li>
                                <div>
                                    <p>
                                        Set <i>Role name</i>
                                    </p>
                                    <CopyCode
                                        value={
                                            this.awsInfo.roleName ||
                                            "FormantAccessRole"
                                        }
                                    />
                                    <p>
                                        Note: This role must be named{" "}
                                        <code>FormantAccessRole</code>
                                    </p>
                                    <p>
                                        Click <b>Create role</b>
                                    </p>
                                </div>
                            </li>
                        </ol>
                    </div>
                ),
            },
        ];

        return (
            <div
                className={classnames(
                    styles["edit-aws-info"],
                    this.hidden && styles.hidden,
                )}
            >
                <div className={styles.header}>
                    <Heading3 className={styles.heading}>
                        Configure AWS S3 Integration
                    </Heading3>
                    <IconButton
                        icon="close"
                        className={styles.close}
                        onClicked={this.onClose}
                    />
                </div>

                <ContentTabs tabs={tabs} />
                <ValidationMessages validation={this.validation} />
                <Buttons className={styles.buttons}>
                    <StandardButton primary={true} onClicked={this.onSave}>
                        Save and close
                    </StandardButton>
                </Buttons>
            </div>
        );
    }

    private onChangeAccountId = (event: ChangeEvent<InputElement>) => {
        this.awsInfo.accountId = event.target.value;
    };

    private onChangeBucket = (event: ChangeEvent<InputElement>) => {
        this.awsInfo.s3Export = {
            ...(this.awsInfo.s3Export || {}),
            bucket: event.target.value,
        };
    };

    private onChangePath = (event: ChangeEvent<InputElement>) => {
        this.awsInfo.s3Export = {
            bucket: "",
            ...(this.awsInfo.s3Export || {}),
            path: event.target.value,
        };
    };

    private onClose = async () => {
        const { onClose } = this.props;
        this.hidden = true;
        await animate();
        onClose();
    };

    private onSave = async () => {
        const { awsInfo } = this;
        const { organizationId, onClose } = this.props;
        try {
            await organizationStore.update(organizationId, {
                awsInfo,
            });
            this.hidden = true;
            await animate();
            onClose();
        } catch (error) {
            const responseError = narrowError(error, ResponseError);
            if (responseError.statusCode === 422) {
                this.validation = responseError.validationErrors;
            } else {
                throw error;
            }
        }
    };
}
